import { ArrowPathIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { regenerateCityPage, toggleCityPageEnabled } from "../../services/CityPages.service";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import { Row } from "../ui/table";
import Can from "../users/permissions/Can";

interface CityPagesTableRowProps {
  row: {
    id: number;
    cityName: string;
    countryName: string;
    hasContent: boolean;
    isEnabled: boolean;
    totalLinePages: number;
  };
  pageIndex: string;
  onRefetch: () => void;
}

const REGENERATE_CONFIRM =
  "Ky veprim do të rigjenerojë statistikat dhe FAQ për këtë faqe qyteti.";

const CityPagesTableRow: React.FC<CityPagesTableRowProps> = ({
  row,
  pageIndex,
  onRefetch
}) => {
  const [toggling, setToggling] = useState(false);

  const handleToggleEnabled = async () => {
    setToggling(true);
    try {
      await toggleCityPageEnabled(row.id);
      toast.success(
        row.isEnabled
          ? "Faqja e qytetit u çaktivizua me sukses."
          : "Faqja e qytetit u aktivizua me sukses."
      );
      onRefetch();
    } catch {
      toast.error("Ndodhi një gabim gjatë ndryshimit të statusit.");
    } finally {
      setToggling(false);
    }
  };

  return (
    <Row
      key={row.id}
      id={row.id}
      className={`${+pageIndex === row.id ? "highlighted" : ""}`}
      cityName={row.cityName}
      countryName={row.countryName}
      hasContent={
        row.hasContent ? (
          <span className={
            "inline-block rounded-full bg-green-100 "
            + "px-2 py-0.5 text-xs font-medium text-green-700"
          }>
            Ka përmbajtje
          </span>
        ) : (
          <span className={
            "inline-block rounded-full bg-gray-100 "
            + "px-2 py-0.5 text-xs font-medium text-gray-500"
          }>
            Pa përmbajtje
          </span>
        )
      }
      status={
        <Can permissions="citypages.citypage.update">
          <button
            type="button"
            disabled={toggling}
            onClick={handleToggleEnabled}
            className={
              "inline-flex items-center gap-1.5 rounded-full "
              + "px-2.5 py-0.5 text-xs font-medium "
              + "cursor-pointer transition-colors "
              + (row.isEnabled
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-red-100 text-red-700 hover:bg-red-200")
            }
          >
            <span
              className={
                "inline-block h-1.5 w-1.5 rounded-full "
                + (row.isEnabled ? "bg-green-500" : "bg-red-500")
              }
            />
            {row.isEnabled ? "Aktiv" : "Joaktiv"}
          </button>
        </Can>
      }
      totalLinePages={row.totalLinePages}
      actions={
        <span className="flex gap-2">
          <Can permissions="citypages.citypage.update">
            <Link to={`/city-pages/edit/${row.id}/sections`}>
              <IconBtn className="h-6 w-6"><PencilIcon className="h-4 w-4 text-neutral" /></IconBtn>
            </Link>
          </Can>
          <Can permissions="citypages.citypage.update">
            <DeleteModal
              params={row.id}
              title="Konfirmo rigjenerimin"
              paragraph={REGENERATE_CONFIRM}
              handleDelete={regenerateCityPage}
              successMessage="Faqja e qytetit u rigjenerua me sukses."
              errorMessage="Ndodhi një gabim gjatë rigjenerimit."
              loadingMessage="Duke rigjeneruar faqen e qytetit..."
              onSuccess={onRefetch}
              onError={onRefetch}
              indicator={
                <IconBtn className="h-6 w-6"><ArrowPathIcon className="h-4 w-4 text-neutral" /></IconBtn>
              }
            />
          </Can>
        </span>
      }
    />
  );
};

export default CityPagesTableRow;
